import { useState } from "react"
import { Plus } from "lucide-react"
import { api, errorMessage } from "../../api/client"
import { useApp } from "../../state/AppState"
import { Button, Card, Field, Input } from "../ui"
import { RichTextEditor } from "../editor/RichTextEditor"
import { TagSelector } from "./TagSelector"

/** Raise a new clarification. Title is required; everything else is optional. */
export function IssueComposer({
	projectId,
	tags,
	canCreateTags = true,
	onTagCreated,
	onCancel,
	onCreated
}) {
	const { toast, actor } = useApp()
	const [title, setTitle] = useState("")
	const [body, setBody] = useState("")
	const [tagIds, setTagIds] = useState([])
	const [attachments, setAttachments] = useState([])
	const [saving, setSaving] = useState(false)
	const [error, setError] = useState(null)

	async function submit(event) {
		event.preventDefault()
		if (!title.trim()) {
			setError("Every issue needs a one-line question or statement.")
			return
		}
		setSaving(true)
		try {
			const { issue } = await api.createIssue(projectId, {
				title: title.trim(),
				body,
				tag_ids: tagIds,
				attachment_ids: attachments.map((a) => a.attachment_id)
			})
			onCreated(issue)
		} catch (cause) {
			// The field error sits under the title, so a body problem is a toast only.
			if (cause.code !== "body_too_long") setError(cause.message)
			toast(errorMessage(cause), "error")
		} finally {
			setSaving(false)
		}
	}

	return (
		<Card className="animate-fade-in p-4">
			<form
				onSubmit={submit}
				onKeyDown={(event) => {
					// Ctrl+Enter raises it from the title or the detail.
					if (event.key === "Enter" && (event.ctrlKey || event.metaKey)) {
						event.preventDefault()
						if (!saving) event.currentTarget.requestSubmit()
					}
				}}
				className="space-y-3.5"
			>
				<Field label="What needs clarifying?" error={error}>
					<Input
						value={title}
						onChange={(event) => setTitle(event.target.value)}
						placeholder="Which cladding specification applies to the east elevation?"
						autoFocus
						maxLength={300}
					/>
				</Field>

				<div>
					<span className="mb-1.5 block text-[0.8125rem] font-medium text-ink-700">
						Detail
					</span>
					<RichTextEditor
						projectId={projectId}
						value={body}
						onChange={setBody}
						attachments={attachments}
						onAttachmentsChange={setAttachments}
						placeholder="Add the context: which drawing, which email, what the conflict is. Paste a screenshot straight in."
					/>
				</div>

				<div>
					<span className="mb-1.5 block text-[0.8125rem] font-medium text-ink-700">
						Tags
					</span>
					<TagSelector
						projectId={projectId}
						tags={tags}
						selected={tagIds}
						onChange={setTagIds}
						onTagCreated={onTagCreated}
						canCreate={canCreateTags}
					/>
				</div>

				<div className="flex items-center justify-between gap-3 border-t border-ink-100 pt-3">
					<p className="text-2xs leading-relaxed text-ink-500">
						Raising as <span className="font-medium text-ink-700">{actor?.name}</span>.
						You can keep editing until someone replies.
					</p>
					<div className="flex shrink-0 gap-2">
						<Button type="button" onClick={onCancel}>
							Cancel
						</Button>
						<Button
							type="submit"
							variant="primary"
							icon={Plus}
							busy={saving}
							disabled={saving}
							title="Ctrl+Enter"
						>
							Raise issue
						</Button>
					</div>
				</div>
			</form>
		</Card>
	)
}
